// @ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { BIKES, BRANDS } from "./bikes";
import { calculateFitTransfer } from "./calculateFitTransfer";
import { Analytics } from "@vercel/analytics/react";

// ============================================================
// BRAND TOKENS — newbikeday.fit
// Fonts loaded on index.html:
//   Bebas Neue (display), DM Mono (UI/labels), DM Sans (body)
// ============================================================

const C = {
  navy: "#04080f",
  navyMid: "#080e1a",
  navyLight: "#0e1828",
  navyRim: "#1e3055",
  accent: "#e8ff47",
  white: "#f4f6fa",
  muted: "#6b7fa3",
  mid: "#8da3c4",
  border: "rgba(255,255,255,0.07)",
  borderM: "rgba(255,255,255,0.11)",
};

const MM_PER_INCH = 25.4;

// Border radius reference:
//   0px   — cards, buttons, tooltip panels, warning blocks
//   8px   — text inputs, selects, dropdowns, custom input
//   20px  — category filter pills
//   6px   — QuickSelect option pills
//   4px   — delta badges (shorter / longer / add spacers)

// ============================================================
// SHARED STYLE OBJECTS
// ============================================================

const inputStyle = {
  width: "100%",
  background: C.navyLight,
  border: `1px solid ${C.borderM}`,
  borderRadius: "8px",
  padding: "12px 16px",
  color: C.white,
  fontFamily: "'DM Mono', monospace",
  fontSize: "13px",
  letterSpacing: "0.04em",
  outline: "none",
  boxSizing: "border-box",
};

const cardStyle = {
  background: C.navyMid,
  border: `1px solid ${C.border}`,
  borderRadius: 0,
  padding: "32px",
  marginBottom: "2px",
};

const labelStyle = {
  display: "block",
  fontFamily: "'DM Mono', monospace",
  fontSize: "9.5px",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: C.muted,
  marginBottom: "8px",
};

const eyebrowStyle = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "9.5px",
  letterSpacing: "0.26em",
  textTransform: "uppercase",
  color: C.accent,
  marginBottom: "20px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

// ============================================================
// SMALL COMPONENTS
// ============================================================

function Eyebrow({ children }) {
  return (
    <div style={eyebrowStyle}>
      {children}
      <span
        style={{ flex: 1, height: "1px", background: C.accent, opacity: 0.25 }}
      />
    </div>
  );
}

function Divider() {
  return (
    <div style={{ borderTop: `1px solid ${C.border}`, margin: "24px 0" }} />
  );
}

function ResultRow({ label, explanation, oldVal, newVal, changed, badge, imperial }) {
  const unit = imperial ? "in" : "mm";
  const fmtVal = (v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return v;
    return imperial ? (num / MM_PER_INCH).toFixed(2) : v;
  };
  return (
    <div
      style={{
        padding: "16px",
        background: C.navyLight,
        borderLeft: `3px solid ${C.accent}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: 1 }}>
        <p
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: C.mid,
            marginBottom: "4px",
          }}
        >
          {label}
        </p>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "11px",
            color: C.muted,
            fontWeight: 300,
            lineHeight: 1.6,
          }}
        >
          {explanation}
        </p>
      </div>
      <div
        style={{
          textAlign: "right",
          marginLeft: "16px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {changed && (
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "11px",
              color: C.muted,
            }}
          >
            {fmtVal(oldVal)}{unit}
          </span>
        )}
        <span
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "28px",
            letterSpacing: "0.05em",
            color: C.accent,
          }}
        >
          {fmtVal(newVal)} {unit}
        </span>
        {!changed && (
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "10px",
              color: C.muted,
            }}
          >
            (unchanged)
          </span>
        )}
        {badge}
      </div>
    </div>
  );
}

function UnitToggle({ imperial, onChange }) {
  return (
    <div style={{ display: "flex", gap: "4px", marginLeft: "auto" }}>
      {[false, true].map((isImperial) => {
        const label = isImperial ? "in" : "mm";
        const active = imperial === isImperial;
        return (
          <button
            key={label}
            onClick={() => onChange(isImperial)}
            style={{
              padding: "3px 10px",
              borderRadius: "4px",
              fontFamily: "'DM Mono', monospace",
              fontSize: "9px",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              border: `1px solid ${active ? C.accent : C.borderM}`,
              background: active ? "rgba(232,255,71,0.1)" : "transparent",
              color: active ? C.accent : C.muted,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// SEARCH SELECT
// ============================================================

function SearchSelect({
  label,
  placeholder,
  value,
  onChange,
  options,
  disabled,
}) {
  const [search, setSearch] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setSearch(value || "");
  }, [value]);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <label style={labelStyle}>{label}</label>
      <input
        style={{
          ...inputStyle,
          opacity: disabled ? 0.4 : 1,
          cursor: disabled ? "not-allowed" : "text",
        }}
        placeholder={
          disabled ? `Select ${placeholder} first` : `Search ${placeholder}...`
        }
        value={search}
        disabled={disabled}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
          onChange("");
        }}
        onFocus={() => !disabled && setOpen(true)}
      />
      {open && filtered.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 9999,
            marginTop: 4,
            background: C.navyMid,
            border: `1px solid ${C.borderM}`,
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            maxHeight: "192px",
            overflowY: "auto",
          }}
        >
          {filtered.map((o) => (
            <div
              key={o}
              style={{
                padding: "10px 16px",
                color: C.mid,
                fontFamily: "'DM Mono', monospace",
                fontSize: "12px",
                letterSpacing: "0.04em",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.navyLight;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
              onMouseDown={() => {
                onChange(o);
                setSearch(o);
                setOpen(false);
              }}
            >
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TOOLTIPS
// ============================================================

const TOOLTIPS = {
  saddleHeight: {
    title: "Saddle Height",
    what: "The vertical distance from the centre of the bottom bracket to the top of the saddle.",
    how: "Measure from the centre of the BB axle up to the top of the saddle at its centre point. Keep the tape parallel to the seat tube.",
  },
  saddleSetback: {
    title: "Saddle Setback",
    what: "Where your saddle tip sits relative to the BB. Almost all road riders are negative — saddle tip forward of BB. Example: -96mm means 96mm forward of the BB.",
    how: "Drop a plumb line from the BB centre. Measure horizontally to the saddle tip. Forward of the line = negative (e.g. -96mm). Behind = positive (uncommon).",
  },
  stemLength: {
    title: "Stem Length",
    what: "Centre of steerer clamp to centre of handlebar clamp, measured along the stem body.",
    how: "Usually stamped on the stem (e.g. '100mm'). If not, measure between the two clamp centres.",
  },
  stemAngle: {
    title: "Stem Angle",
    what: "Angle relative to horizontal. Negative (e.g. -6 degrees) angles the stem downward. Positive angles it upward (flipped stem). Most road stems are -6 to -17 degrees.",
    how: "Usually stamped on the stem (e.g. '+/-6' or '-17'). If not, use a digital angle gauge on the flat top of the stem.",
  },
  barDrop: {
    title: "Handlebar Drop",
    what: "Vertical distance from bar clamp centre to the bottom of the drops. Typical road bars: 120–145mm.",
    how: "Measure vertically from the clamp centre to the bottom of the drop. Usually in the bar spec sheet.",
  },
  barReach: {
    title: "Handlebar Reach",
    what: "Horizontal distance from bar clamp centre to the furthest point of the drops. Typical road bars: 70–90mm.",
    how: "Measure horizontally from the clamp centre to the tip of the drops. Usually in the bar spec sheet.",
  },
  crankLength: {
    title: "Crank Length",
    what: "Centre of BB axle to centre of pedal axle. Typical road: 165–175mm. Different lengths require saddle height adjustment to maintain the same knee angle.",
    how: "Usually printed on the inside face of the crank arm (e.g. '172.5'). If not visible, measure from BB axle centre to pedal thread centre.",
  },
  spacers: {
    title: "Spacers Under Stem",
    what: "Total height of headset spacers between the fork steerer top and the stem bottom. More spacers = higher bars.",
    how: "Stack the spacers and measure their combined height. Or measure the gap between the top of the headset and the bottom of the stem.",
  },
};

function Tooltip({ id }) {
  const [open, setOpen] = useState(false);
  const tip = TOOLTIPS[id];
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "18px",
          height: "18px",
          background: C.navyRim,
          border: `1px solid ${C.borderM}`,
          borderRadius: 0,
          color: C.muted,
          fontSize: "10px",
          fontFamily: "'DM Mono', monospace",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "border-color 0.15s ease, color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = C.accent;
          e.currentTarget.style.color = C.accent;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = C.borderM;
          e.currentTarget.style.color = C.muted;
        }}
        title="More info"
      >
        i
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "24px",
            zIndex: 50,
            width: "288px",
            background: C.navyMid,
            border: `1px solid ${C.borderM}`,
            borderRadius: 0,
            padding: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
          }}
        >
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "10px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: C.accent,
              marginBottom: "10px",
            }}
          >
            {tip.title}
          </p>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "12px",
              lineHeight: 1.7,
              color: C.muted,
              fontWeight: 300,
              marginBottom: "8px",
            }}
          >
            <span style={{ color: C.mid, fontWeight: 400 }}>What it is: </span>
            {tip.what}
          </p>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "12px",
              lineHeight: 1.7,
              color: C.muted,
              fontWeight: 300,
            }}
          >
            <span style={{ color: C.mid, fontWeight: 400 }}>
              How to measure:{" "}
            </span>
            {tip.how}
          </p>
        </div>
      )}
    </div>
  );
}

function LabelWithTooltip({ label, tooltipId }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
      }}
    >
      <label
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "11px",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: C.muted,
        }}
      >
        {label}
      </label>
      <Tooltip id={tooltipId} />
    </div>
  );
}

// ============================================================
// BIKE SELECTOR
// ============================================================

const ALL_CATEGORIES = ["All", "Road", "Gravel", "CX", "TT", "MTB"];

function getAvailableCategories() {
  const available = new Set();
  Object.values(BIKES).forEach((brand) => {
    Object.values(brand.models).forEach((model) => {
      if (model.category) available.add(model.category);
    });
  });
  return available;
}

function getBrandsForCategory(category) {
  if (category === "All")
    return BRANDS.slice().sort((a, b) => a.localeCompare(b));
  return BRANDS.filter((brand) =>
    Object.values(BIKES[brand].models).some((m) => m.category === category)
  ).sort((a, b) => a.localeCompare(b));
}

function getModelsForBrandAndCategory(brand, category) {
  if (!brand) return [];
  const keys =
    category === "All"
      ? Object.keys(BIKES[brand].models)
      : Object.keys(BIKES[brand].models).filter(
          (key) => BIKES[brand].models[key].category === category
        );
  return keys.sort((a, b) => {
    const baseName = (k) => k.replace(/\s*\(.*?\)/, "").trim();
    const nameCompare = baseName(a).localeCompare(baseName(b));
    if (nameCompare !== 0) return nameCompare;
    const getYear = (k) => {
      const m = k.match(/\((\d{4})/);
      return m ? parseInt(m[1]) : 0;
    };
    return getYear(b) - getYear(a);
  });
}

function BikeSelector({
  label,
  brand,
  setBrand,
  model,
  setModel,
  size,
  setSize,
}) {
  const [category, setCategory] = useState("All");
  const availableCategories = getAvailableCategories();
  const filteredBrands = getBrandsForCategory(category);
  const models = brand ? getModelsForBrandAndCategory(brand, category) : [];
  const sizes =
    brand && model ? Object.keys(BIKES[brand].models[model].sizes) : [];

  const handleCategory = (cat) => {
    if (cat !== "All" && !availableCategories.has(cat)) return;
    setCategory(cat);
    if (cat !== "All" && brand && !getBrandsForCategory(cat).includes(brand)) {
      setBrand("");
      setModel("");
      setSize("");
    } else if (brand) {
      const newModels = getModelsForBrandAndCategory(brand, cat);
      if (!newModels.includes(model)) {
        setModel("");
        setSize("");
      }
    }
  };
  const handleBrand = (b) => {
    setBrand(b);
    setModel("");
    setSize("");
  };
  const handleModel = (m) => {
    setModel(m);
    if (m && brand) setSize(Object.keys(BIKES[brand].models[m].sizes)[0]);
  };

  return (
    <div style={{ overflow: "visible" }}>
      <p
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: "28px",
          letterSpacing: "0.04em",
          color: C.white,
          marginBottom: "20px",
          lineHeight: 1,
        }}
      >
        {label}
      </p>

      {/* Category filter pills */}
      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>Category</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {ALL_CATEGORIES.map((cat) => {
            const isAvailable = cat === "All" || availableCategories.has(cat);
            const isActive = category === cat;
            return (
              <button
                key={cat}
                onClick={() => handleCategory(cat)}
                disabled={!isAvailable}
                style={{
                  padding: "6px 14px",
                  borderRadius: "20px",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "10px",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: "500",
                  border: `1px solid ${
                    isActive ? C.accent : isAvailable ? C.borderM : C.border
                  }`,
                  background: isActive ? "rgba(232,255,71,0.1)" : "transparent",
                  color: isActive ? C.accent : isAvailable ? C.mid : C.muted,
                  opacity: isAvailable ? 1 : 0.35,
                  cursor: isAvailable ? "pointer" : "not-allowed",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (isAvailable && !isActive) {
                    e.currentTarget.style.borderColor = C.accent;
                    e.currentTarget.style.color = C.accent;
                  }
                }}
                onMouseLeave={(e) => {
                  if (isAvailable && !isActive) {
                    e.currentTarget.style.borderColor = C.borderM;
                    e.currentTarget.style.color = C.mid;
                  }
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 10, marginBottom: "12px" }}>
        <SearchSelect
          label="Brand"
          placeholder="brand"
          value={brand}
          onChange={handleBrand}
          options={filteredBrands}
          disabled={false}
        />
      </div>
      <div style={{ position: "relative", zIndex: 9, marginBottom: "12px" }}>
        <SearchSelect
          label="Model & Year"
          placeholder="model"
          value={model}
          onChange={handleModel}
          options={models}
          disabled={!brand}
        />
      </div>

      {sizes.length > 0 && (
        <div style={{ marginTop: "12px" }}>
          <label style={labelStyle}>Frame Size</label>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            style={{
              ...inputStyle,
              cursor: "pointer",
            }}
          >
            {sizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ marginTop: "16px" }}>
        <Link
          to="/request-a-bike"
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "10px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: C.muted,
            textDecoration: "none",
            transition: "color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = C.accent;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = C.muted;
          }}
        >
          Can't find your bike? Request it here &rarr;
        </Link>
      </div>
    </div>
  );
}

// ============================================================
// INPUT COMPONENTS
// ============================================================

const RANGES = {
  saddleHeight: { min: 600, max: 1000, label: "Saddle height" },
  saddleSetback: { min: -150, max: 100, label: "Saddle setback" },
};

function useFieldValidation(value, fieldKey, imperial) {
  const range = RANGES[fieldKey];
  if (!range || value === "") return null;
  const num = parseFloat(value);
  if (isNaN(num)) return { type: "error", msg: "Please enter a number" };
  const displayNum = imperial ? (num / MM_PER_INCH).toFixed(2) : num;
  const unitSuffix = imperial ? "in" : "mm";
  if (num < range.min || num > range.max)
    return {
      type: "warn",
      msg: `${range.label} of ${displayNum}${unitSuffix} looks unusual — double-check your measurement`,
    };
  if (fieldKey === "saddleSetback" && num > 0)
    return {
      type: "warn",
      msg: "Positive setback is uncommon for road riding — most riders are negative (saddle tip forward of BB). Are you sure?",
    };
  return null;
}

function ValidatedInput({ value, onChange, fieldKey, inputMode, placeholder, imperial }) {
  const [touched, setTouched] = useState(false);

  const toDisplay = (mmStr) => {
    if (mmStr === "" || mmStr === undefined) return "";
    const num = parseFloat(mmStr);
    if (isNaN(num)) return mmStr;
    return imperial ? (num / MM_PER_INCH).toFixed(2) : mmStr;
  };

  const [display, setDisplay] = useState(() => toDisplay(value));
  const justChangedRef = useRef(false);

  // Re-sync display when unit toggles
  // eslint-disable-next-line
  useEffect(() => {
    setDisplay(toDisplay(value));
    setTouched(false);
  }, [imperial]);

  // Re-sync display when value is changed externally (e.g. loading a profile)
  // eslint-disable-next-line
  useEffect(() => {
    if (!justChangedRef.current) {
      setDisplay(toDisplay(value));
    }
    justChangedRef.current = false;
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setDisplay(raw);
    justChangedRef.current = true;
    if (raw === "" || raw === "-") {
      onChange("");
      return;
    }
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      onChange(imperial ? String(+(num * MM_PER_INCH).toFixed(1)) : raw);
    }
  };

  const validation = useFieldValidation(value, fieldKey, imperial);
  const showValidation = touched && validation;
  const borderColor = showValidation
    ? validation.type === "error"
      ? "#ef4444"
      : "#f59e0b"
    : C.borderM;
  return (
    <div>
      <input
        type="text"
        value={display}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        inputMode={inputMode || "decimal"}
        placeholder={placeholder}
        style={{ ...inputStyle, border: `1px solid ${borderColor}` }}
      />
      {showValidation && (
        <p
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px",
            letterSpacing: "0.04em",
            color: validation.type === "error" ? "#ef4444" : "#f59e0b",
            marginTop: "6px",
          }}
        >
          {validation.msg}
        </p>
      )}
    </div>
  );
}

function QuickSelect({ options, value, onChange, placeholder, inputMode, imperial }) {
  // isCustom: always compare mm values vs mm options
  const isCustom = value !== "" && !options.map(String).includes(String(value));
  const [showCustom, setShowCustom] = useState(isCustom);

  const toDisplayVal = (mmStr) => {
    if (!mmStr) return "";
    const num = parseFloat(mmStr);
    if (isNaN(num)) return mmStr;
    return imperial ? (num / MM_PER_INCH).toFixed(2) : mmStr;
  };

  const [customDisplay, setCustomDisplay] = useState(() =>
    isCustom ? toDisplayVal(value) : ""
  );
  const prevImperialRef = useRef(imperial);
  useEffect(() => {
    if (prevImperialRef.current !== imperial) {
      prevImperialRef.current = imperial;
      if (showCustom && value !== "") {
        setCustomDisplay(toDisplayVal(value));
      }
    }
  });

  const handlePill = (opt) => {
    if (opt === "Custom") {
      setShowCustom(true);
      setCustomDisplay("");
      onChange("");
    } else {
      setShowCustom(false);
      setCustomDisplay("");
      onChange(String(opt)); // always store mm value
    }
  };

  const handleCustomChange = (e) => {
    const raw = e.target.value;
    setCustomDisplay(raw);
    if (raw === "" || raw === "-") {
      onChange("");
      return;
    }
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      onChange(imperial ? String(+(num * MM_PER_INCH).toFixed(1)) : raw);
    }
  };

  const activeVal = showCustom ? "Custom" : String(value);
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {[...options, "Custom"].map((opt) => {
          const active = String(opt) === activeVal;
          const label =
            opt === "Custom" || !imperial
              ? String(opt)
              : (opt / MM_PER_INCH).toFixed(2);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => handlePill(opt)}
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px",
                letterSpacing: "0.1em",
                border: `1px solid ${active ? C.accent : C.borderM}`,
                background: active ? "rgba(232,255,71,0.1)" : "transparent",
                color: active ? C.accent : C.mid,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.borderColor = C.accent;
                  e.currentTarget.style.color = C.accent;
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.borderColor = C.borderM;
                  e.currentTarget.style.color = C.mid;
                }
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      {showCustom && (
        <input
          style={{ ...inputStyle, marginTop: "8px" }}
          type="text"
          inputMode={inputMode || "decimal"}
          placeholder={placeholder}
          value={customDisplay}
          onChange={handleCustomChange}
          autoFocus
        />
      )}
    </div>
  );
}

function WarningCard({ warning }) {
  const isRed = warning.level === "red";
  return (
    <div
      style={{
        padding: "12px 16px",
        border: `1px solid ${
          isRed ? "rgba(239,68,68,0.3)" : "rgba(245,158,11,0.3)"
        }`,
        borderRadius: 0,
        background: isRed ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)",
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
      }}
    >
      <span style={{ fontSize: "14px", flexShrink: 0 }}>
        {isRed ? "🚫" : "⚠️"}
      </span>
      <p
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "12px",
          lineHeight: 1.65,
          fontWeight: 300,
          color: isRed ? "#fca5a5" : "#fcd34d",
        }}
      >
        {warning.msg}
      </p>
    </div>
  );
}

// ============================================================
// DELTA BADGE
// ============================================================

function deltaBadge(color, borderColor, bg) {
  return {
    fontFamily: "'DM Mono', monospace",
    fontSize: "9px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    padding: "4px 8px",
    borderRadius: "4px",
    border: `1px solid ${borderColor}`,
    color,
    background: bg,
  };
}

// ============================================================
// MAIN APP
// ============================================================

export default function BikeTransferTool() {
  const [curBrand, setCurBrand] = useState("");
  const [curModel, setCurModel] = useState("");
  const [curSize, setCurSize] = useState("");
  const [saddleHeight, setSaddleHeight] = useState("");
  const [saddleSetback, setSaddleSetback] = useState("");
  const [stemLength, setStemLength] = useState("");
  const [stemAngle, setStemAngle] = useState("");
  const [spacers, setSpacers] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const [newModel, setNewModel] = useState("");
  const [newSize, setNewSize] = useState("");
  const [newStemAngle, setNewStemAngle] = useState("-6");
  const [crankLength, setCrankLength] = useState("172.5");
  const [newCrankLength, setNewCrankLength] = useState("172.5");

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advCrank, setAdvCrank] = useState(true);
  const [advBarDrop, setAdvBarDrop] = useState(true);
  const [advBarReach, setAdvBarReach] = useState(true);
  const [barDrop, setBarDrop] = useState("125");
  const [newBarDrop, setNewBarDrop] = useState("125");
  const [barReach, setBarReach] = useState("80");
  const [newBarReach, setNewBarReach] = useState("80");

  const [imperial, setImperial] = useState(false);
  const unit = imperial ? "in" : "mm";
  const fmtMm = (mm) => {
    const num = parseFloat(mm);
    if (isNaN(num)) return mm;
    return imperial ? (num / MM_PER_INCH).toFixed(2) : String(mm);
  };

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const [formError, setFormError] = useState("");

  const [profiles, setProfiles] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bikefit_profiles") || "{}");
    } catch {
      return {};
    }
  });
  const [saveNameDraft, setSaveNameDraft] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  const autoName = () =>
    curBrand && curModel && curSize
      ? `${curBrand} ${curModel} · ${curSize}`
      : "";

  const saveProfile = () => {
    if (!saveNameDraft.trim()) return;
    const profile = {
      curBrand,
      curModel,
      curSize,
      saddleHeight,
      saddleSetback,
      stemLength,
      stemAngle,
      spacers,
    };
    const updated = { ...profiles, [saveNameDraft.trim()]: profile };
    setProfiles(updated);
    localStorage.setItem("bikefit_profiles", JSON.stringify(updated));
    setSaveNameDraft("");
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const loadProfile = (name) => {
    const p = profiles[name];
    if (!p) return;
    setCurBrand(p.curBrand);
    setCurModel(p.curModel);
    setCurSize(p.curSize);
    setSaddleHeight(p.saddleHeight);
    setSaddleSetback(p.saddleSetback);
    setStemLength(p.stemLength);
    setStemAngle(p.stemAngle);
    setSpacers(p.spacers);
  };

  const deleteProfile = (name) => {
    const updated = { ...profiles };
    delete updated[name];
    setProfiles(updated);
    localStorage.setItem("bikefit_profiles", JSON.stringify(updated));
  };

  const canCalc =
    curBrand &&
    curModel &&
    curSize &&
    newBrand &&
    newModel &&
    newSize &&
    saddleHeight !== "" &&
    saddleSetback !== "" &&
    stemLength !== "" &&
    stemAngle !== "" &&
    spacers !== "" &&
    newStemAngle !== "" &&
    (!showAdvanced ||
      !advCrank ||
      (crankLength !== "" && newCrankLength !== "")) &&
    (!showAdvanced || !advBarDrop || (barDrop !== "" && newBarDrop !== "")) &&
    (!showAdvanced || !advBarReach || (barReach !== "" && newBarReach !== ""));

  const calculate = () => {
    setFormError("");
    setCalcError(null);
    setResults(null);
    if (!canCalc) {
      setFormError("Please complete all fields before calculating.");
      return;
    }

    const cg = BIKES[curBrand].models[curModel].sizes[curSize];
    const ng = BIKES[newBrand].models[newModel].sizes[newSize];
    const curGeo = {
      stack: cg.stack,
      reach: cg.reach,
      seatTubeAngle:
        cg.seatTubeAngle || BIKES[curBrand].models[curModel].seatTubeAngle,
    };
    const newGeo = {
      stack: ng.stack,
      reach: ng.reach,
      seatTubeAngle:
        ng.seatTubeAngle || BIKES[newBrand].models[newModel].seatTubeAngle,
    };

    const result = calculateFitTransfer({
      curGeo,
      newGeo,
      saddleHeight,
      saddleSetback,
      stemLength,
      stemAngle,
      spacers,
      newStemAngle,
      barDrop: showAdvanced && advBarDrop ? barDrop : "125",
      barReach: showAdvanced && advBarReach ? barReach : "80",
      newBarDrop: showAdvanced && advBarDrop ? newBarDrop : "125",
      newBarReach: showAdvanced && advBarReach ? newBarReach : "80",
      crankLength: showAdvanced && advCrank ? crankLength : "172.5",
      newCrankLength: showAdvanced && advCrank ? newCrankLength : "172.5",
    });

    if (result.error) {
      setCalcError(result);
      return;
    }
    setResults(result);
  };

  return (
    <div
      style={{ minHeight: "100vh", background: C.navy, padding: "0 24px 64px" }}
    >
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
        {/* ── NAV BAR ── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 0 8px",
            borderBottom: `1px solid ${C.border}`,
            marginBottom: "0",
          }}
        >
          {/* Wordmark */}
          <Link
            to="/"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "13px",
              letterSpacing: "0.06em",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <span style={{ color: C.white }}>newbikeday</span>
            <span style={{ color: C.accent }}>.fit</span>
          </Link>

          {/* Back link */}
          <Link
            to="/"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "10px",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: C.muted,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "color 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = C.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = C.muted;
            }}
          >
            &larr; Home
          </Link>
        </div>

        {/* ── PAGE HEADER ── */}
        <div style={{ textAlign: "center", padding: "48px 0 48px" }}>
          <div
            style={{
              ...eyebrowStyle,
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <span
              style={{
                width: "24px",
                height: "1px",
                background: C.accent,
                opacity: 0.4,
                display: "inline-block",
              }}
            />
            Fit Transfer Tool
            <span
              style={{
                width: "24px",
                height: "1px",
                background: C.accent,
                opacity: 0.4,
                display: "inline-block",
              }}
            />
          </div>
          <h1
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(48px, 8vw, 80px)",
              letterSpacing: "0.03em",
              lineHeight: 0.9,
              color: C.white,
              margin: "0 0 20px",
            }}
          >
            Bike Fit Transfer
          </h1>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "14px",
              lineHeight: 1.75,
              color: C.muted,
              fontWeight: 300,
              margin: 0,
            }}
          >
            Transfer your professional fit to any new bike.
          </p>
        </div>

        {/* ── CURRENT BIKE CARD ── */}
        <div
          style={{
            ...cardStyle,
            overflow: "visible",
            position: "relative",
            zIndex: 20,
          }}
        >
          {/* Saved profiles */}
          {Object.keys(profiles).length > 0 && (
            <div
              style={{
                marginBottom: "24px",
                paddingBottom: "24px",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <Eyebrow>Load Saved Profile</Eyebrow>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {Object.keys(profiles).map((name) => (
                  <div
                    key={name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <button
                      onClick={() => loadProfile(name)}
                      style={{
                        flex: 1,
                        textAlign: "left",
                        padding: "10px 16px",
                        background: "transparent",
                        border: `1px solid ${C.borderM}`,
                        borderRadius: 0,
                        color: C.mid,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "12px",
                        letterSpacing: "0.04em",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = C.accent;
                        e.currentTarget.style.color = C.white;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = C.borderM;
                        e.currentTarget.style.color = C.mid;
                      }}
                    >
                      {name}
                    </button>
                    <button
                      onClick={() => deleteProfile(name)}
                      style={{
                        padding: "10px 14px",
                        background: "transparent",
                        border: "1px solid transparent",
                        borderRadius: 0,
                        color: C.muted,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "11px",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#ef4444";
                        e.currentTarget.style.borderColor =
                          "rgba(239,68,68,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = C.muted;
                        e.currentTarget.style.borderColor = "transparent";
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <BikeSelector
            label="Current Bike"
            brand={curBrand}
            setBrand={setCurBrand}
            model={curModel}
            setModel={setCurModel}
            size={curSize}
            setSize={setCurSize}
          />

          {curBrand && curModel && curSize && (
            <div>
              <Divider />
              <Eyebrow>
                Current Fit Measurements
                <UnitToggle imperial={imperial} onChange={setImperial} />
              </Eyebrow>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <LabelWithTooltip
                    label={`Saddle Height (${unit})`}
                    tooltipId="saddleHeight"
                  />
                  <ValidatedInput
                    placeholder={imperial ? "e.g. 31.3" : "e.g. 794"}
                    value={saddleHeight}
                    onChange={(e) => setSaddleHeight(e.target.value)}
                    fieldKey="saddleHeight"
                    imperial={imperial}
                  />
                </div>
                <div>
                  <LabelWithTooltip
                    label={`Saddle Setback (${unit})`}
                    tooltipId="saddleSetback"
                  />
                  <ValidatedInput
                    placeholder={imperial ? "e.g. -3.78 (negative = forward of BB)" : "e.g. -96 (negative = forward of BB)"}
                    value={saddleSetback}
                    onChange={(e) => setSaddleSetback(e.target.value)}
                    fieldKey="saddleSetback"
                    inputMode="text"
                    imperial={imperial}
                  />
                </div>
                <div>
                  <LabelWithTooltip
                    label={`Stem Length (${unit})`}
                    tooltipId="stemLength"
                  />
                  <QuickSelect
                    options={[70, 80, 90, 100, 110, 120, 130]}
                    value={stemLength}
                    onChange={setStemLength}
                    placeholder="e.g. 105"
                    imperial={imperial}
                  />
                </div>
                <div>
                  <LabelWithTooltip
                    label="Stem Angle (degrees)"
                    tooltipId="stemAngle"
                  />
                  <QuickSelect
                    options={[0, -6, -8, -10, -12, -17]}
                    value={stemAngle}
                    onChange={setStemAngle}
                    placeholder="e.g. -7"
                    inputMode="text"
                  />
                </div>
                <div>
                  <LabelWithTooltip
                    label={`Spacers Under Stem (${unit})`}
                    tooltipId="spacers"
                  />
                  <QuickSelect
                    options={[0, 5, 10, 15, 20, 25, 30]}
                    value={spacers}
                    onChange={setSpacers}
                    placeholder="e.g. 12"
                    imperial={imperial}
                  />
                </div>
              </div>

              <Divider />

              {/* Advanced options toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  background: "none",
                  border: "none",
                  borderRadius: 0,
                  padding: 0,
                  cursor: "pointer",
                  width: "100%",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "10px",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: C.muted,
                  transition: "color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = C.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = C.muted;
                }}
              >
                <span
                  style={{
                    transition: "transform 0.2s ease",
                    transform: showAdvanced ? "rotate(90deg)" : "none",
                    display: "inline-block",
                  }}
                >
                  &#9654;
                </span>
                Advanced options
                {!showAdvanced && (
                  <span
                    style={{ color: C.muted, opacity: 0.5, fontSize: "9px" }}
                  >
                    bar profile · crank length
                  </span>
                )}
                {showAdvanced && (
                  <span
                    style={{
                      marginLeft: "auto",
                      color: C.muted,
                      opacity: 0.6,
                      fontSize: "9px",
                    }}
                  >
                    Using explicit bar and crank values for more accurate
                    results.
                  </span>
                )}
              </button>

              {showAdvanced && (
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  {[
                    {
                      checked: advCrank,
                      setChecked: setAdvCrank,
                      label: `Crank Length (${unit})`,
                      tip: "crankLength",
                      opts: [165, 167.5, 170, 172.5, 175, 177.5],
                      val: crankLength,
                      set: setCrankLength,
                      ph: "e.g. 172.5",
                    },
                    {
                      checked: advBarDrop,
                      setChecked: setAdvBarDrop,
                      label: `Handlebar Drop (${unit})`,
                      tip: "barDrop",
                      opts: [115, 120, 125, 128, 130, 135, 140, 145],
                      val: barDrop,
                      set: setBarDrop,
                      ph: "e.g. 125",
                    },
                    {
                      checked: advBarReach,
                      setChecked: setAdvBarReach,
                      label: `Handlebar Reach (${unit})`,
                      tip: "barReach",
                      opts: [70, 75, 80, 85, 90, 95],
                      val: barReach,
                      set: setBarReach,
                      ph: "e.g. 80",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{ opacity: item.checked ? 1 : 0.4 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginBottom: "8px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={(e) => item.setChecked(e.target.checked)}
                          style={{ cursor: "pointer", accentColor: C.accent }}
                        />
                        <LabelWithTooltip
                          label={item.label}
                          tooltipId={item.tip}
                        />
                      </div>
                      <div
                        style={{
                          pointerEvents: item.checked ? "auto" : "none",
                        }}
                      >
                        <QuickSelect
                          options={item.opts}
                          value={item.val}
                          onChange={item.set}
                          placeholder={item.ph}
                          imperial={imperial}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Divider />
              <Eyebrow>Save This Profile</Eyebrow>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="Profile name"
                  value={saveNameDraft}
                  onChange={(e) => setSaveNameDraft(e.target.value)}
                  onFocus={() => {
                    if (!saveNameDraft) setSaveNameDraft(autoName());
                  }}
                />
                <button
                  onClick={saveProfile}
                  style={{
                    padding: "12px 20px",
                    borderRadius: 0,
                    background: savedFlash ? "#22c55e" : C.accent,
                    border: "none",
                    color: savedFlash ? C.white : C.navy,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "11px",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    if (!savedFlash) {
                      e.currentTarget.style.opacity = "0.9";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.transform = "none";
                  }}
                >
                  {savedFlash ? "Saved" : "Save"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── NEW BIKE CARD ── */}
        <div
          style={{
            ...cardStyle,
            overflow: "visible",
            position: "relative",
            zIndex: 10,
          }}
        >
          <BikeSelector
            label="New Bike"
            brand={newBrand}
            setBrand={setNewBrand}
            model={newModel}
            setModel={setNewModel}
            size={newSize}
            setSize={setNewSize}
          />

          {newBrand && newModel && newSize && (
            <div>
              <Divider />

              {/* Integrated cockpit warning */}
              {BIKES[newBrand].models[newModel].cockpitType ===
                "integrated" && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "14px 16px",
                    background: "rgba(245,158,11,0.08)",
                    border: "1px solid rgba(245,158,11,0.25)",
                    borderRadius: 0,
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ fontSize: "14px", flexShrink: 0 }}>
                    &#9888;
                  </span>
                  <div>
                    <p
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "10px",
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "#fcd34d",
                        marginBottom: "6px",
                      }}
                    >
                      Integrated Cockpit
                    </p>
                    <p
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "12px",
                        lineHeight: 1.65,
                        color: C.muted,
                        fontWeight: 300,
                      }}
                    >
                      This bike uses a proprietary integrated bar/stem. The stem
                      length output is a target reach — achieve it by adjusting
                      the bar position on the integrated cockpit rather than
                      swapping a standard stem.
                    </p>
                  </div>
                </div>
              )}

              <Eyebrow>New Bike Setup</Eyebrow>
              <div>
                <label
                  style={{
                    ...labelStyle,
                    fontSize: "11px",
                    letterSpacing: "0.14em",
                  }}
                >
                  New Stem Angle (degrees)
                </label>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "12px",
                    color: C.muted,
                    fontWeight: 300,
                    marginBottom: "10px",
                    lineHeight: 1.6,
                  }}
                >
                  Negative = angled down (standard). Positive = angled up
                  (flipped stem).
                </p>
                <QuickSelect
                  options={[0, -6, -8, -10, -12, -17]}
                  value={newStemAngle}
                  onChange={setNewStemAngle}
                  placeholder="e.g. -7"
                  inputMode="text"
                />
              </div>

              {showAdvanced && (
                <div style={{ marginTop: "20px" }}>
                  <Divider />
                  <Eyebrow>New Bike Advanced</Eyebrow>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    {advCrank && (
                      <div>
                        <LabelWithTooltip
                          label={`New Crank Length (${unit})`}
                          tooltipId="crankLength"
                        />
                        <QuickSelect
                          options={[165, 167.5, 170, 172.5, 175, 177.5]}
                          value={newCrankLength}
                          onChange={setNewCrankLength}
                          placeholder="e.g. 172.5"
                          imperial={imperial}
                        />
                      </div>
                    )}
                    {advBarDrop && (
                      <div>
                        <LabelWithTooltip
                          label={`New Handlebar Drop (${unit})`}
                          tooltipId="barDrop"
                        />
                        <QuickSelect
                          options={[115, 120, 125, 128, 130, 135, 140, 145]}
                          value={newBarDrop}
                          onChange={setNewBarDrop}
                          placeholder="e.g. 125"
                          imperial={imperial}
                        />
                      </div>
                    )}
                    {advBarReach && (
                      <div>
                        <LabelWithTooltip
                          label={`New Handlebar Reach (${unit})`}
                          tooltipId="barReach"
                        />
                        <QuickSelect
                          options={[70, 75, 80, 85, 90, 95]}
                          value={newBarReach}
                          onChange={setNewBarReach}
                          placeholder="e.g. 80"
                          imperial={imperial}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── FORM ERROR ── */}
        {formError && (
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.08em",
              color: "#ef4444",
              margin: "12px 0",
              padding: "0 4px",
            }}
          >
            {formError}
          </p>
        )}

        {/* ── CTA BUTTON ── */}
        <button
          onClick={calculate}
          style={{
            width: "100%",
            padding: "18px",
            borderRadius: 0,
            background: canCalc ? C.accent : C.navyLight,
            border: `1px solid ${canCalc ? C.accent : C.border}`,
            color: canCalc ? C.navy : C.muted,
            fontFamily: "'DM Mono', monospace",
            fontSize: "12px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            fontWeight: "500",
            cursor: canCalc ? "pointer" : "not-allowed",
            transition: "all 0.15s ease",
            marginTop: "2px",
            marginBottom: "2px",
          }}
          onMouseEnter={(e) => {
            if (canCalc) {
              e.currentTarget.style.opacity = "0.9";
              e.currentTarget.style.transform = "translateY(-2px)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
            e.currentTarget.style.transform = "none";
          }}
        >
          Calculate Transfer
        </button>

        {/* ── CALC ERROR ── */}
        {calcError && (
          <div style={{ ...cardStyle, marginTop: "2px" }}>
            <div
              style={{
                padding: "16px",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)",
                borderRadius: 0,
                display: "flex",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "18px", flexShrink: 0 }}>&#128683;</span>
              <div>
                <p
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "10px",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "#ef4444",
                    marginBottom: "8px",
                  }}
                >
                  {calcError.error === "STEM_ANGLE_OUT_OF_RANGE"
                    ? "Invalid Stem Angle"
                    : "Frame Incompatibility"}
                </p>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "13px",
                    lineHeight: 1.65,
                    color: C.mid,
                    fontWeight: 300,
                  }}
                >
                  {calcError.message}
                </p>
                {calcError.barX_cur && (
                  <p
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      color: C.muted,
                      marginTop: "8px",
                    }}
                  >
                    Your target bar position: {calcError.barX_cur}mm forward,{" "}
                    {calcError.barY_cur}mm up from BB.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── RESULTS ── */}
        {results && (
          <div style={{ ...cardStyle, marginTop: "2px" }}>
            <p
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "36px",
                letterSpacing: "0.04em",
                color: C.white,
                marginBottom: "24px",
                lineHeight: 1,
              }}
            >
              Your New Setup
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                marginBottom: "24px",
              }}
            >
              {/* Saddle Height */}
              <ResultRow
                label="Saddle Height"
                explanation={results.explanations.saddleHeight}
                oldVal={saddleHeight}
                newVal={results.newSaddleHeight}
                changed={results.crankDiff !== 0}
                imperial={imperial}
              />

              {/* Saddle Setback */}
              <ResultRow
                label="Saddle Setback"
                explanation={results.explanations.setback}
                oldVal={saddleSetback}
                newVal={results.newSetback}
                changed={results.staShift !== 0}
                imperial={imperial}
              />

              {/* Stem Length */}
              <div
                style={{
                  padding: "16px",
                  background: C.navyLight,
                  borderLeft: `3px solid ${C.accent}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: C.mid,
                      marginBottom: "4px",
                    }}
                  >
                    Stem Length
                  </p>
                  <p
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "11px",
                      color: C.muted,
                      fontWeight: 300,
                      lineHeight: 1.6,
                    }}
                  >
                    {results.explanations.stemLength}
                  </p>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    marginLeft: "16px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      color: C.muted,
                    }}
                  >
                    {fmtMm(stemLength)}{unit}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: "28px",
                      letterSpacing: "0.05em",
                      color: C.accent,
                    }}
                  >
                    {fmtMm(results.newStemLength)} {unit}
                  </span>
                  {results.newStemLength !== parseInt(stemLength) && (
                    <span
                      style={deltaBadge(
                        results.newStemLength < parseInt(stemLength)
                          ? "#93c5fd"
                          : "#fdba74",
                        results.newStemLength < parseInt(stemLength)
                          ? "rgba(96,165,250,0.3)"
                          : "rgba(251,146,60,0.3)",
                        results.newStemLength < parseInt(stemLength)
                          ? "rgba(96,165,250,0.08)"
                          : "rgba(251,146,60,0.08)"
                      )}
                    >
                      {results.newStemLength < parseInt(stemLength)
                        ? "shorter"
                        : "longer"}
                    </span>
                  )}
                </div>
              </div>

              {/* Spacers */}
              <div
                style={{
                  padding: "16px",
                  background: C.navyLight,
                  borderLeft: `3px solid ${C.accent}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: C.mid,
                      marginBottom: "4px",
                    }}
                  >
                    Spacers Under Stem
                  </p>
                  <p
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "11px",
                      color: C.muted,
                      fontWeight: 300,
                      lineHeight: 1.6,
                    }}
                  >
                    {results.explanations.spacers}
                  </p>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    marginLeft: "16px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      color: C.muted,
                    }}
                  >
                    {fmtMm(spacers)}{unit}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: "28px",
                      letterSpacing: "0.05em",
                      color: C.accent,
                    }}
                  >
                    {fmtMm(results.newSpacers)} {unit}
                  </span>
                  {results.newSpacers !== parseInt(spacers) && (
                    <span
                      style={deltaBadge(
                        results.newSpacers > parseInt(spacers)
                          ? "#86efac"
                          : "#fca5a5",
                        results.newSpacers > parseInt(spacers)
                          ? "rgba(74,222,128,0.3)"
                          : "rgba(239,68,68,0.3)",
                        results.newSpacers > parseInt(spacers)
                          ? "rgba(74,222,128,0.08)"
                          : "rgba(239,68,68,0.08)"
                      )}
                    >
                      {results.newSpacers > parseInt(spacers)
                        ? "add spacers"
                        : "remove spacers"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Warnings */}
            {results.warnings.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  marginBottom: "20px",
                }}
              >
                {results.warnings.map((w, i) => (
                  <WarningCard key={i} warning={w} />
                ))}
              </div>
            )}

            {/* Seatpost recommendation */}
            <div
              style={{
                padding: "16px",
                background: "rgba(167,139,250,0.08)",
                border: "1px solid rgba(167,139,250,0.2)",
                borderRadius: 0,
                marginBottom: "8px",
              }}
            >
              <p
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "9.5px",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "#c4b5fd",
                  marginBottom: "8px",
                }}
              >
                Seatpost Recommendation
              </p>
              <p
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "24px",
                  letterSpacing: "0.04em",
                  color: C.white,
                  marginBottom: "6px",
                }}
              >
                {results.seatpostRec}
              </p>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "12px",
                  lineHeight: 1.65,
                  color: C.muted,
                  fontWeight: 300,
                }}
              >
                {results.seatpostReason}
              </p>
            </div>

            {/* Honesty note */}
            <div
              style={{
                padding: "16px 20px",
                background: "rgba(232,255,71,0.05)",
                border: `1px solid ${C.borderM}`,
                borderRadius: 0,
                display: "flex",
                gap: "16px",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "10px",
                  color: C.accent,
                  letterSpacing: "0.1em",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Note →
              </span>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "12px",
                  lineHeight: 1.65,
                  color: C.muted,
                  fontWeight: 300,
                }}
              >
                These numbers are a calculated starting point — not a final fit.
                Ride the bike, make small adjustments, and have a fitter verify
                on the bike if possible.
              </p>
            </div>
          </div>
        )}
      </div>
      <Analytics />
    </div>
  );
}
